import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Clock, Calendar, Twitter, Linkedin, Link2, User, MessageSquare, ThumbsUp, Send, Mail, CheckCircle, ExternalLink } from "lucide-react";
import { getRelatedPosts } from "../data/blogPosts";
import BlogCard from "./BlogCard";
import AuthorBox from "./AuthorBox";

import { useTranslation } from 'react-i18next';
function ShareButtons({ title, url }) {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  return (
    <div className="flex items-center gap-2 sm:gap-3">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
        Share:
      </span>
      <a
        href={`https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 border border-zinc-800 hover:border-amber-500 text-zinc-400 hover:text-amber-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
        aria-label={t('article_layout.share_on_twitter')}
      >
        <Twitter className="w-4 h-4" aria-hidden="true" />
      </a>
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-2 border border-zinc-800 hover:border-amber-500 text-zinc-400 hover:text-amber-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
        aria-label={t('article_layout.share_on_linkedin')}
      >
        <Linkedin className="w-4 h-4" aria-hidden="true" />
      </a>
      <button
        onClick={() => navigator.clipboard?.writeText(url)}
        className="p-2 border border-zinc-800 hover:border-amber-500 text-zinc-400 hover:text-amber-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
        aria-label={t('article_layout.copy_link')}
      >
        <Link2 className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
}

function TableOfContents({ content }) {
  const [tocOpen, setTocOpen] = useState(false);
  const headings = content
    .split("\n")
    .filter((line) => line.trim().startsWith("## "))
    .map((line) => line.trim().replace("## ", ""));

  if (headings.length < 2) return null;

  return (
    <nav className="border border-zinc-900 p-4 sm:p-6 mb-6 sm:mb-8" aria-label={t('article_layout.table_of_contents')}>
      <button
        type="button"
        onClick={() => setTocOpen(!tocOpen)}
        className="flex items-center justify-between w-full lg:cursor-default"
        aria-expanded={tocOpen}
      >
        <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500">{t('article_layout.table_of_contents')}</h3>
        <span className="lg:hidden text-zinc-500">
          {tocOpen ? "−" : "+"}
        </span>
      </button>
      <ol className={`space-y-2 mt-4 ${tocOpen ? "block" : "hidden"} lg:block`}>
        {headings.map((h, i) => (
          <li key={i}>
            <a
              href={`#section-${i}`}
              onClick={() => setTocOpen(false)}
              className="text-sm text-zinc-400 hover:text-amber-500 transition-colors"
            >
              {h}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

// ── Newsletter Inline CTA ────────────────────────────────────────────
function NewsletterInlineCTA() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="border border-emerald-500/30 bg-emerald-500/5 p-6 my-8 flex items-center gap-3" role="alert">
        <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" aria-hidden="true" />
        <p className="text-emerald-400 text-sm">{t('article_layout.youre_in_check_your_inbox_for_a_confirmation_email')}</p>
      </div>
    );
  }

  return (
    <div className="border border-amber-500/20 bg-amber-500/5 p-4 sm:p-6 my-6 sm:my-8" aria-label={t('article_layout.newsletter_signup')}>
      <div className="flex items-start gap-3">
        <Mail className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" aria-hidden="true" />
        <div className="flex-1">
          <h4 className="font-display text-sm uppercase text-white mb-1">{t('article_layout.enjoying_this_article')}</h4>
          <p className="text-zinc-400 text-xs leading-relaxed mb-3">{t('article_layout.get_weekly_ai_agent_insights_delivered_to_your')}</p>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
            <label htmlFor="newsletter-inline-email" className="sr-only">{t('article_layout.email_address')}</label>
            <input
              id="newsletter-inline-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="flex-1 bg-black/50 border border-zinc-800 px-3 py-2 text-sm text-white placeholder-zinc-600 font-mono outline-none focus:border-amber-500 transition-colors min-h-[44px]"
            />
            <button
              type="submit"
              className="bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 font-bold uppercase tracking-wider text-[10px] transition-colors shrink-0 min-h-[44px]"
            >{t('article_layout.subscribe')}</button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Inline Product CTA ───────────────────────────────────────────────
function InlineCTA() {
  return (
    <div className="border border-amber-500/30 bg-amber-500/5 p-5 sm:p-8 my-8 sm:my-12 text-center">
      <h3 className="font-display text-lg sm:text-xl uppercase text-white mb-2">{t('article_layout.ready_to_deploy_your_ai_agent')}</h3>
      <p className="text-zinc-400 text-sm mb-6 max-w-lg mx-auto">
        Start your free 7-day trial. No credit card required. Deploy in 5 minutes.
      </p>
      <Link
        to="/pricing"
        className="inline-block bg-amber-500 hover:bg-amber-400 text-black px-8 py-3 font-bold uppercase tracking-wider text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
      >
        Start Free Trial
      </Link>
    </div>
  );
}

function CommentSection() {
  const [comments, setComments] = useState([
    {
      id: 1,
      name: "Alex M.",
      date: "2 hours ago",
      text: "Great article! We deployed AI agents for our support team last month and already seeing 50% reduction in response time.",
      likes: 12,
    },
    {
      id: 2,
      name: "Sarah K.",
      date: "5 hours ago",
      text: "The ROI breakdown is really helpful. We were on the fence about AI agents but this convinced us to start the trial.",
      likes: 8,
    },
  ]);
  const [newComment, setNewComment] = useState("");
  const [likedComments, setLikedComments] = useState(new Set());

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setComments((prev) => [
      {
        id: Date.now(),
        name: "Guest",
        date: "Just now",
        text: newComment.trim(),
        likes: 0,
      },
      ...prev,
    ]);
    setNewComment("");
  };

  const handleLike = (id) => {
    if (likedComments.has(id)) return;
    setLikedComments((prev) => new Set(prev).add(id));
    setComments((prev) =>
      prev.map((c) => (c.id === id ? { ...c, likes: c.likes + 1 } : c))
    );
  };

  return (
    <section className="mt-12 border-t border-zinc-900 pt-8" aria-label={t('article_layout.comments')}>
      <h3 className="flex items-center gap-2 font-display text-lg uppercase text-white mb-6">
        <MessageSquare className="w-5 h-5 text-amber-500" aria-hidden="true" />
        Discussion ({comments.length})
      </h3>

      {/* Comment form */}
      <form onSubmit={handleSubmit} className="mb-8">
        <label htmlFor="comment-textarea" className="sr-only">{t('article_layout.write_a_comment')}</label>
        <textarea
          id="comment-textarea"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder={t('article_layout.share_your_thoughts')}
          rows={3}
          className="w-full bg-black/50 border border-zinc-800 p-4 text-sm text-white placeholder-zinc-600 font-mono outline-none focus:border-amber-500 transition-colors resize-none"
        />
        <div className="flex justify-end mt-3">
          <button
            type="submit"
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 font-bold uppercase tracking-wider text-[10px] transition-colors"
          >
            <Send className="w-3 h-3" /> Post Comment
          </button>
        </div>
      </form>

      {/* Comments list */}
      <div className="space-y-6">
        {comments.map((comment) => (
          <div key={comment.id} className="border border-zinc-900 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-zinc-800 flex items-center justify-center">
                  <User className="w-4 h-4 text-zinc-400" />
                </div>
                <span className="font-mono text-xs text-white">{comment.name}</span>
                <span className="font-mono text-[9px] text-zinc-500">{comment.date}</span>
              </div>
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed mb-3">{comment.text}</p>
            <button
              onClick={() => handleLike(comment.id)}
              className={`flex items-center gap-1 text-xs transition-colors ${
                likedComments.has(comment.id)
                  ? "text-amber-500"
                  : "text-zinc-500 hover:text-amber-500"
              }`}
              aria-label={`Like comment (${comment.likes} likes)`}
            >
              <ThumbsUp className="w-3 h-3" />
              {comment.likes}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Estimated Reading Time helper ────────────────────────────────────
function estimateReadingTime(content) {
  if (!content) return "1 min read";
  const words = content.trim().split(/\s+/).length;
  const minutes = Math.max(1, Math.ceil(words / 200));
  return `${minutes} min read`;
}

export default function ArticleLayout({ post, slug, content: contentProp }) {
  const { t } = useTranslation();
  if (!post) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-4xl uppercase mb-4">{t('article_layout.article_not_found')}</h1>
          <p className="text-zinc-400 mb-8">{t('article_layout.the_article_youre_looking_for_doesnt_exist')}</p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 font-mono text-xs uppercase tracking-[0.2em]"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const postContent = contentProp || post.content;

  const relatedPosts = getRelatedPosts(slug, 6);
  const articleUrl = `https://maxemployee.com/blog/${slug}`;
  const readingTime = post.readTime || estimateReadingTime(postContent);

  // Simple markdown-like rendering: convert ## headings and paragraphs
  // Now with newsletter CTA injected after 2nd paragraph
  const renderContent = (content) => {
    const lines = content.trim().split("\n");
    const elements = [];
    let inTable = false;
    let tableRows = [];
    let h2Index = 0;
    let paragraphCount = 0;
    let newsletterInjected = false;

    const flushTable = () => {
      if (tableRows.length > 0) {
        const headerRow = tableRows[0];
        const dataRows = tableRows.slice(2); // skip separator row
        elements.push(
          <div key={`table-${elements.length}`} className="overflow-x-auto my-8">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-zinc-700">
                  {headerRow.map((cell, i) => (
                    <th
                      key={i}
                      className="text-left py-3 px-4 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500"
                    >
                      {cell.trim()}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dataRows.map((row, ri) => (
                  <tr key={ri} className="border-b border-zinc-900">
                    {row.map((cell, ci) => (
                      <td key={ci} className="py-3 px-4 text-zinc-300">
                        {cell.trim()}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        tableRows = [];
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (line === "") {
        if (inTable) {
          flushTable();
          inTable = false;
        }
        continue;
      }

      // Table detection
      if (line.startsWith("|") && line.endsWith("|")) {
        if (!inTable) inTable = true;
        const cells = line.split("|").filter((c) => c.trim() !== "");
        tableRows.push(cells);
        continue;
      } else if (inTable) {
        flushTable();
        inTable = false;
      }

      // H2
      if (line.startsWith("## ")) {
        const headingText = line.replace("## ", "");
        const sectionId = `section-${h2Index}`;
        h2Index++;
        elements.push(
          <h2
            key={`h2-${i}`}
            id={sectionId}
            className="font-display text-2xl md:text-3xl uppercase text-white mt-12 mb-6 leading-tight scroll-mt-20"
          >
            {headingText}
          </h2>
        );
        continue;
      }

      // H3
      if (line.startsWith("### ")) {
        elements.push(
          <h3
            key={`h3-${i}`}
            className="font-display text-xl uppercase text-white mt-8 mb-4"
          >
            {line.replace("### ", "")}
          </h3>
        );
        continue;
      }

      // Bold text with ** markers
      const processInlineFormatting = (text) => {
        const parts = text.split(/(\*\*[^*]+\*\*)/g);
        return parts.map((part, pi) => {
          if (part.startsWith("**") && part.endsWith("**")) {
            return (
              <strong key={pi} className="text-white font-semibold">
                {part.slice(2, -2)}
              </strong>
            );
          }
          return part;
        });
      };

      // List items
      if (line.startsWith("- ")) {
        elements.push(
          <li key={`li-${i}`} className="text-zinc-400 leading-relaxed ml-4 mb-2 list-disc list-inside">
            {processInlineFormatting(line.replace("- ", ""))}
          </li>
        );
        continue;
      }

      // Regular paragraph
      paragraphCount++;
      elements.push(
        <p key={`p-${i}`} className="text-zinc-400 leading-relaxed mb-4">
          {processInlineFormatting(line)}
        </p>
      );

      // Inject newsletter CTA after 2nd paragraph
      if (paragraphCount === 2 && !newsletterInjected) {
        elements.push(<NewsletterInlineCTA key="newsletter-inline" />);
        newsletterInjected = true;
      }
    }

    if (inTable) flushTable();

    return elements;
  };

  return (
    <article>
      {/* Hero */}
      <div className="relative border-b border-zinc-900">
        <div className="absolute inset-0">
          <img
            src={post.ogImage}
            alt={post.title}
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#09090B]/60 via-[#09090B]/80 to-[#09090B]" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 pt-8 sm:pt-12 pb-10 sm:pb-16">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-amber-500 font-mono text-xs uppercase tracking-[0.2em] mb-8 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Back to Blog
          </Link>

          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="bg-amber-500/10 border border-amber-500/30 px-2 py-1 font-mono text-[9px] uppercase tracking-wider text-amber-500">
              {post.category || post.tag}
            </span>
            {post.tags && post.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="px-2 py-1 border border-zinc-800 text-[9px] font-mono uppercase tracking-wider text-zinc-500">
                {tag}
              </span>
            ))}
          </div>

          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl uppercase leading-[0.95] text-white mb-4 sm:mb-6">
            {post.title}
          </h1>

          <p className="text-base sm:text-lg text-zinc-400 leading-relaxed max-w-3xl mb-6 sm:mb-8">
            {post.excerpt}
          </p>

          <div className="flex flex-wrap items-center gap-4">
            {post.author && (
              <Link
                to={`/blog/author/${encodeURIComponent(post.author.name)}`}
                className="flex items-center gap-2 text-zinc-400 hover:text-amber-500 transition-colors"
              >
                <User className="w-4 h-4" aria-hidden="true" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em]">
                  {post.author.name}
                </span>
              </Link>
            )}
            <div className="flex items-center gap-2 text-zinc-400">
              <Calendar className="w-4 h-4" aria-hidden="true" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em]">
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
            </div>
            <div className="flex items-center gap-2 text-zinc-400">
              <Clock className="w-4 h-4" aria-hidden="true" />
              <span className="font-mono text-[10px] uppercase tracking-[0.2em]">
                {readingTime}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Article body */}
          <div className="lg:col-span-8">
            <TableOfContents content={postContent} />
            <div className="prose-custom">
              {renderContent(postContent)}
            </div>

            {/* Inline CTA after content */}
            <InlineCTA />

            {/* Share */}
            <div className="border-t border-zinc-900 pt-6 mt-8">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <ShareButtons title={post.title} url={articleUrl} />
              </div>
            </div>

            {/* Dedicated Author Box */}
            <AuthorBox author={post.author} />

            {/* Comments */}
            <CommentSection />
          </div>

          {/* Sidebar placeholder — rendered by parent page */}
          <div className="lg:col-span-4 hidden" />
        </div>
      </div>

      {/* Related Posts — 4-6 articles */}
      {relatedPosts.length > 0 && (
        <section className="border-t border-zinc-900 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-16">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4">
              // RELATED ARTICLES
            </div>
            <h2 className="font-display text-2xl sm:text-3xl uppercase text-white mb-6 sm:mb-12">{t('article_layout.continue_reading')}</h2>
            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6`}>
              {relatedPosts.map((rp) => (
                <BlogCard key={rp.slug} post={rp} />
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
